import React, { useState, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";

import DPCard from "../../packages/DPCard";
import { cloudUploadIcon } from "../../assets";
import { FILE_UPLOAD } from "../../constants/global.constants";
import { getSupportedFileTypes } from "../../utils/appUtils";
import SupportedFileInfo from "./SupportedFileInfo";

import "./FileUploadCard.scss";

const FileUploadCard = ({
  id,
  hoverable,
  setUploadedFile,
  isProceed,
  setIsProceed,
  onInterrupt,
  shouldInterrupt,
  size,
  disabled,
  showDownload,
}) => {
  const { t } = useTranslation();
  const [isDragging, setIsDragging] = useState(false);
  const [tempFile, setTempFile] = useState(null);

  useEffect(() => {
    if (isProceed) {
      if (tempFile) {
        setUploadedFile(tempFile); // Proceed with the drop action
        setTempFile(null); // Clear the temporary file
      } else {
        document.getElementById(id)?.click();
      }
      setIsProceed(false);
    }
  }, [isProceed, tempFile, id]);

  const handleDragEnter = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      // Check if disabled or multiple files are being dragged
      const items = e.dataTransfer?.items;
      if (disabled || (items && items.length > 1)) {
        e.dataTransfer.dropEffect = "none"; // Show "not-allowed" cursor
        return;
      }
      setIsDragging(true);
    },
    [disabled]
  );

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();

      // Check if disabled or multiple files are being dragged
      const items = e.dataTransfer?.items;
      if (disabled || (items && items.length > 1)) {
        e.dataTransfer.dropEffect = "none"; // Show "not-allowed" cursor
        return;
      }
    },
    [disabled]
  );

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      if (disabled) {
        return;
      }

      const files = Array.from(e.dataTransfer.files);
      // Just handle single file, ignore drop if multiple files
      if (files.length === 1) {
        const file = files[0];
        if (shouldInterrupt) {
          onInterrupt?.(e);
          setTempFile(file); // Store the file temporarily
        } else {
          setUploadedFile(file);
        }
        e.target.value = "";
      }
    },
    [disabled, onInterrupt, shouldInterrupt]
  );

  return (
    <DPCard
      border={isDragging ? "solid" : "dashed"}
      hoverable={hoverable}
      disabled={disabled}
      caption={<SupportedFileInfo showDownload={showDownload} />}
      onCardClick={(e) =>
        shouldInterrupt && !isProceed ? onInterrupt?.(e) : undefined
      }
      size={size}
    >
      <label
        id={id}
        className={`file-drop-zone ${isDragging ? "dragging" : ""} ${
          disabled ? "cip-disabled" : ""
        }`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <input
          type="file"
          className="file-input"
          onChange={(e) => {
            if (disabled) return;
            const file = e.target.files?.[0];
            setUploadedFile(file);
            e.target.value = "";
          }}
          disabled={disabled}
          accept={getSupportedFileTypes(
            FILE_UPLOAD.SUPPORTED_FILE_TYPES,
            ".",
            true
          )}
        />
        <img src={cloudUploadIcon} alt="Cloud Upload Icon" className="icon" />
        <p className="upload-description">
          {isDragging
            ? t("fileUpload.dropHere")
            : t("fileUpload.dropSheetHere")}{" "}
          {!isDragging && (
            <span
              className={`browse-file ${disabled ? "cip-disabled" : ""}`}
              disabled={disabled}
            >
              {t("fileUpload.browseFiles")}
            </span>
          )}
        </p>
      </label>
    </DPCard>
  );
};

export default FileUploadCard;
